"use client"

import { useState, useEffect } from "react"
import { Check, ChevronDown, Upload, Calendar } from "lucide-react"
import axiosInstance from "../../../../axiosConfig" // Ensure this path is correct

// ================ PRIMARY MERCHANT ASSIGNMENT COMPONENT ================
function PrimaryMerchantAssignment({ formData, setFormData }) {
  const [showFromCalendar, setShowFromCalendar] = useState(false)
  const [showToCalendar, setShowToCalendar] = useState(false)
  const [merchants, setMerchants] = useState([])
  const [loadingMerchants, setLoadingMerchants] = useState(false)
  const [roles, setRoles] = useState([])
  const [loadingRoles, setLoadingRoles] = useState(false)

  // Fetch merchants
  useEffect(() => {
    const fetchMerchants = async () => {
      setLoadingMerchants(true)
      try {
        const response = await axiosInstance.get(
          '/internal-api/v1/client-web/entity-panel/config/list',
          {
            params: {
              'districtIds[]': [1, 2],
              'provinceIds[]': [1]
            }
          }
        )
        
        if (response.data && response.data.data) {
          setMerchants(response.data.data)
        }
      } catch (error) {
        console.error("Error fetching merchants:", error)
        alert("Failed to load merchants. Please refresh the page.")
      } finally {
        setLoadingMerchants(false)
      }
    }

    fetchMerchants()
  }, [])

  // Fetch roles (for display only — actual value will be overridden)
  useEffect(() => {
    const fetchRoles = async () => {
      setLoadingRoles(true)
      try {
        const response = await axiosInstance.get('/internal-api/v1/client-web/user-config/filter-meta')
        if (response.data && response.data.result && response.data.result.roles) {
          setRoles(response.data.result.roles)
        }
      } catch (error) {
        console.error("Error fetching roles:", error)
      } finally {
        setLoadingRoles(false)
      }
    }

    fetchRoles()
  }, [])

  const toggleNoExpiry = () => {
    setFormData(prev => ({
      ...prev,
      noExpiry: !prev.noExpiry,
      validTo: prev.noExpiry ? "" : prev.validTo
    }))
  }

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return ""
    const [year, month, day] = dateString.split("-")
    return `${year}-${month}-${day}`
  }

  const DatePicker = ({ onSelect, onClose, currentValue }) => {
    const today = new Date()
    const initialDate = currentValue ? new Date(currentValue) : today

    const [currentMonth, setCurrentMonth] = useState(initialDate.getMonth())
    const [currentYear, setCurrentYear] = useState(initialDate.getFullYear())

    const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate()
    const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay()

    const renderCalendarDays = () => {
      const daysInMonth = getDaysInMonth(currentYear, currentMonth)
      const firstDay = getFirstDayOfMonth(currentYear, currentMonth)
      const days = []

      for (let i = 0; i < firstDay; i++) {
        days.push(<div key={`empty-${i}`} className="w-8 h-8"></div>)
      }

      for (let day = 1; day <= daysInMonth; day++) {
        const isToday =
          day === today.getDate() &&
          currentMonth === today.getMonth() &&
          currentYear === today.getFullYear()

        days.push(
          <div
            key={day}
            onClick={() => {
              const formatted = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`
              onSelect(formatted)
              onClose()
            }}
            className={`w-8 h-8 flex items-center justify-center rounded cursor-pointer hover:bg-brand-blue/10 text-sm ${
              isToday ? "bg-brand-blue text-white" : ""
            }`}
          >
            {day}
          </div>
        )
      }

      return days
    }

    const prevMonth = () => {
      if (currentMonth === 0) {
        setCurrentMonth(11)
        setCurrentYear(currentYear - 1)
      } else {
        setCurrentMonth(currentMonth - 1)
      }
    }

    const nextMonth = () => {
      if (currentMonth === 11) {
        setCurrentMonth(0)
        setCurrentYear(currentYear + 1)
      } else {
        setCurrentMonth(currentMonth + 1)
      }
    }

    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ]

    return (
      <div className="absolute top-full mt-2 bg-white rounded-lg shadow-lg border border-slate-200 p-4 z-50 w-64">
        <div className="flex justify-between items-center mb-4">
          <button onClick={prevMonth} className="p-1 hover:bg-slate-100 rounded">&lt;</button>
          <div className="font-semibold text-sm">
            {monthNames[currentMonth]} {currentYear}
          </div>
          <button onClick={nextMonth} className="p-1 hover:bg-slate-100 rounded">&gt;</button>
        </div>

        <div className="grid grid-cols-7 gap-1 text-xs">
          {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
            <div key={d} className="w-8 h-8 flex items-center justify-center font-medium text-slate-500">
              {d}
            </div>
          ))}
          {renderCalendarDays()}
        </div>

        <div className="flex justify-end gap-2 mt-4 pt-4 border-t text-xs">
          <button
            onClick={() => {
              const t = new Date()
              const f = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`
              onSelect(f)
              onClose()
            }}
            className="px-3 py-1 bg-brand-blue text-white rounded hover:bg-brand-blue-dark"
          >
            Today
          </button>
          <button onClick={onClose} className="px-3 py-1 bg-slate-200 rounded hover:bg-slate-300">
            Cancel
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
      <div className="bg-brand-blue/5 border border-brand-blue/20 rounded-lg p-4 mb-6">
        <p className="text-sm text-brand-blue">
          Creating a user with limited access to specific merchant entity and its destinations
        </p>
      </div>

      <h3 className="text-sm font-bold text-slate-900 mb-6">Primary Merchant Assignment</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1.5">Associated Merchant*</label>
          <select
            value={formData.selectedMerchantId}
            onChange={(e) => {
              const selectedId = e.target.value
              const selectedMerchant = merchants.find(m => m.id.toString() === selectedId)
              setFormData(prev => ({ 
                ...prev, 
                selectedMerchantId: selectedId,
                selectedMerchant: selectedMerchant ? selectedMerchant.name : ""
              }))
            }}
            className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
            disabled={loadingMerchants}
          >
            <option value="">
              {loadingMerchants ? "Loading merchants..." : "Select Merchant"}
            </option>
            {merchants.map((merchant) => (
              <option key={merchant.id} value={merchant.id}>
                {merchant.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1.5">Merchant Role*</label>
          <select
            // This dropdown is for display only — actual value is fixed below
            value={formData.entityAuthRoleCode}
            onChange={(e) => setFormData(prev => ({ ...prev, entityAuthRoleCode: e.target.value }))}
            className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
            disabled={loadingRoles}
          >
            <option value="">
              {loadingRoles ? "Loading roles..." : "Select Role"}
            </option>
            {roles.map((role) => (
              <option key={role.id} value={role.code}>
                {role.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1.5">Access Status*</label>
          <select
            value={formData.isAccessAllowed === "1" ? "Active" : "Inactive"}
            onChange={(e) => setFormData(prev => ({ ...prev, isAccessAllowed: e.target.value === "Active" ? "1" : "0" }))}
            className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
          >
            <option>Active</option>
            <option>Inactive</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-700 mb-1.5">Valid From*</label>
          <div className="relative">
            <input
              type="text"
              value={formatDateForDisplay(formData.validFrom)}
              readOnly
              className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue bg-white cursor-pointer"
            />
            <div className="absolute right-3 top-2.5 cursor-pointer" onClick={() => setShowFromCalendar(!showFromCalendar)}>
              <Calendar size={16} className="text-slate-500" />
            </div>
            {showFromCalendar && (
              <DatePicker
                onSelect={(date) => setFormData(prev => ({ ...prev, validFrom: date }))}
                onClose={() => setShowFromCalendar(false)}
                currentValue={formData.validFrom}
              />
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 max-w-md">
        <label className="block text-xs font-semibold text-slate-700 mb-1.5">Valid Till</label>
        <div className="relative">
          <input
            type="text"
            value={formatDateForDisplay(formData.validTo)}
            readOnly
            disabled={formData.noExpiry}
            placeholder="Select date"
            className={`w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue ${formData.noExpiry ? "bg-slate-100" : "bg-white cursor-pointer"}`}
          />
          {!formData.noExpiry && (
            <div className="absolute right-3 top-2.5 cursor-pointer" onClick={() => setShowToCalendar(!showToCalendar)}>
              <Calendar size={16} className="text-slate-500" />
            </div>
          )}
          {showToCalendar && !formData.noExpiry && (
            <DatePicker
              onSelect={(date) => setFormData(prev => ({ ...prev, validTo: date }))}
              onClose={() => setShowToCalendar(false)}
              currentValue={formData.validTo}
            />
          )}
        </div>
        <label className="flex items-center mt-3 text-sm">
          <input
            type="checkbox"
            checked={formData.noExpiry}
            onChange={toggleNoExpiry}
            className="mr-2"
          />
          No Expiry
        </label>
      </div>
    </div>
  )
}

// ================ MAIN USER CREATION FORM ================
export default function UserCreationForm() {
  const [activeTab, setActiveTab] = useState("basic")
  const [loading, setLoading] = useState(false)
  const [profileImage, setProfileImage] = useState(null)

  const [formData, setFormData] = useState({
    userType: "Entity User",
    firstName: "",
    lastName: "",
    email: "",
    username: "",
    password: "",
    reTypePassword: "",
    isMultiLoginAllowed: "1",
    entityAuthRoleCode: "ROLE_ENTITY_ADMIN", // Fixed value
    isAccessAllowed: "1",
    genderCode: "",
    countryCode: "LK",
    nic: "",
    passportNo: "ewewew2",
    selectedMerchant: "",
    selectedMerchantId: "",
    validFrom: "",
    validTo: "",
    noExpiry: true,
    entityIds: []
  })

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setProfileImage(e.target.files[0])
    }
  }

  const handleSubmit = async () => {
    if (formData.userType !== "Entity User") {
      alert("This form is for Entity Users only.")
      return
    }

    if (!formData.firstName || !formData.lastName || !formData.email || !formData.username || !formData.password) {
      alert("Please fill all required fields.")
      return
    }

    if (!formData.selectedMerchantId) {
      alert("Please select an Associated Merchant.")
      return
    }

    if (formData.password !== formData.reTypePassword) {
      alert("Passwords do not match.")
      return
    }

    setLoading(true)

    const submitData = new FormData()

    // FIXED: Always send ROLE_ENTITY_ADMIN
    submitData.append("entityAuthRoleCode", "ROLE_ENTITY_ADMIN")

    submitData.append("isAccessAllowed", formData.isAccessAllowed)
    submitData.append("username", formData.username)
    submitData.append("password", formData.password)
    submitData.append("reTypePassword", formData.reTypePassword)
    submitData.append("email", formData.email)
    submitData.append("isMultiLoginAllowed", formData.isMultiLoginAllowed)
    submitData.append("firstName", formData.firstName)
    submitData.append("lastName", formData.lastName)
    submitData.append("genderCode", formData.genderCode)
    submitData.append("countryCode", formData.countryCode)
    submitData.append("nic", formData.nic)
    submitData.append("passportNo", formData.passportNo || "")
    if (profileImage) {
      submitData.append("profileImage", profileImage)
    }

    formData.entityIds.forEach(id => {
      submitData.append("entityIds[]", id)
    })

    if (formData.selectedMerchantId) {
      submitData.append("entityIds[]", formData.selectedMerchantId)
    }

    console.log("Submitting FormData:")
    for (let [key, value] of submitData.entries()) {
      console.log(key, value instanceof File ? `File: ${value.name}` : value)
    }

    try {
      const response = await axiosInstance.post(
        "/internal-api/v1/client-web/entity-panel/users/new-user",
        submitData,
        {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        }
      )

      console.log("Success Response:", response.data)

      if (response.data && response.data.success) {
        alert("Entity User created successfully!")
      } else {
        alert("User created but response unclear. Check console.")
      }
    } catch (error) {
      console.error("API Error:", error.response?.data || error.message)
      alert("Failed to create user: " + (error.response?.data?.message || "Please try again."))
    } finally {
      setLoading(false)
    }
  }

  const branches = [
    { id: "13", name: "Sigiriya Office", location: "Sigiriya", status: "Active" },
    { id: "14", name: "Anuradhapura Office", location: "Anuradhapura", status: "Active" },
    { id: "15", name: "Colombo Office", location: "Colombo", status: "Active" },
    { id: "16", name: "Kandy Office", location: "Kandy", status: "Inactive" },
    { id: "17", name: "Galle Office", location: "Galle", status: "Active" }
  ]

  const permissions = [
    { id: "1", module: "Dashboard", view: false, create: false, edit: false, delete: false },
    { id: "2", module: "Users Management", view: false, create: false, edit: false, delete: false },
    { id: "3", module: "Bookings", view: false, create: false, edit: false, delete: false },
    { id: "4", module: "Reports", view: false, create: false, edit: false, delete: false },
    { id: "5", module: "Settings", view: false, create: false, edit: false, delete: false }
  ]

  const [selectedBranches, setSelectedBranches] = useState([])
  const [permissionsState, setPermissionsState] = useState(permissions)

  const toggleBranchSelection = (branchId) => {
    setSelectedBranches(prev => 
      prev.includes(branchId) 
        ? prev.filter(id => id !== branchId)
        : [...prev, branchId]
    )
  }

  const toggleAllBranches = () => {
    if (selectedBranches.length === branches.length) {
      setSelectedBranches([])
    } else {
      setSelectedBranches(branches.map(b => b.id))
    }
  }

  const togglePermission = (permId, permType) => {
    setPermissionsState(prev => 
      prev.map(p => 
        p.id === permId 
          ? { ...p, [permType]: !p[permType] }
          : p
      )
    )
  }

  const toggleAllPermissionsForModule = (permId) => {
    setPermissionsState(prev => 
      prev.map(p => {
        if (p.id === permId) {
          const allChecked = p.view && p.create && p.edit && p.delete
          return { 
            ...p, 
            view: !allChecked, 
            create: !allChecked, 
            edit: !allChecked, 
            delete: !allChecked 
          }
        }
        return p
      })
    )
  }

  const tabs = [
    { id: "basic", label: "Basic Info" },
    { id: "access", label: "Access & Permissions" },
  ];

  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Add New User</h1>
        <div className="mt-1 text-sm text-slate-500">
          Home <span className="mx-1.5 text-slate-400">/</span>
          Users <span className="mx-1.5 text-slate-400">/</span>
          <span className="font-semibold text-slate-700">Add New User</span>
        </div>
      </div>

      <div className="bg-white border rounded-xl border-slate-200">
        {/* Tabs */}
        <div className="px-6 pt-6">
          <div className="flex border-b border-slate-200">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`px-4 py-3 mr-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                  activeTab === tab.id
                    ? "text-brand-blue border-brand-blue"
                    : "text-slate-500 border-transparent hover:text-slate-700"
                }`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Basic Info Tab */}
        {activeTab === "basic" && (
          <div className="p-6 space-y-8">
            <h2 className="mb-4 text-sm font-bold text-slate-900">User Information</h2>

          <div className="mb-6">
            <p className="mb-2 text-slate-700">User Type</p>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  className="mr-2 h-4 w-4 text-brand-blue"
                  checked={formData.userType === "System Admin"}
                  onChange={() => setFormData(prev => ({ ...prev, userType: "System Admin" }))}
                />
                <span className="bg-brand-blue/10 text-brand-blue py-1 px-3 rounded text-xs">System Admin</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  className="mr-2 h-4 w-4 text-brand-blue"
                  checked={formData.userType === "Entity User"}
                  onChange={() => setFormData(prev => ({ ...prev, userType: "Entity User" }))}
                />
                <span className="border border-slate-300 py-1 px-3 rounded text-xs">Entity User</span>
              </label>
            </div>
          </div>

          <h3 className="text-sm font-bold text-slate-900 mb-4">Personal Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">First Name<span className="text-red-500">*</span></label>
              <input type="text" className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                value={formData.firstName}
                onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Last Name<span className="text-red-500">*</span></label>
              <input type="text" className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                value={formData.lastName}
                onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Profile Image</label>
              <label className="border border-slate-300 rounded flex items-center justify-center h-24 bg-slate-50 cursor-pointer">
                <input type="file" className="hidden" accept="image/*" onChange={handleImageChange} />
                <div className="text-center">
                  {profileImage ? (
                    <p className="text-xs text-emerald-600">Selected: {profileImage.name}</p>
                  ) : (
                    <>
                      <p className="text-brand-blue mb-1 text-xs">Upload Image</p>
                      <Upload size={24} className="mx-auto text-brand-blue" />
                    </>
                  )}
                </div>
              </label>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Email Address<span className="text-red-500">*</span></label>
              <input type="email" className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">NIC Number</label>
              <input type="text" className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                value={formData.nic}
                onChange={(e) => setFormData(prev => ({ ...prev, nic: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Gender</label>
              <select className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                value={formData.genderCode}
                onChange={(e) => setFormData(prev => ({ ...prev, genderCode: e.target.value }))}
              >
                <option value="MALE">Male</option>
                <option value="FEMALE">Female</option>
              </select>
            </div>
          </div>

          <h3 className="text-sm font-bold text-slate-900 mb-4">Username & Password</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Username<span className="text-red-500">*</span></label>
              <input type="text" className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                value={formData.username}
                onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Password<span className="text-red-500">*</span></label>
              <input type="password" className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Confirm Password<span className="text-red-500">*</span></label>
              <input type="password" className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                value={formData.reTypePassword}
                onChange={(e) => setFormData(prev => ({ ...prev, reTypePassword: e.target.value }))}
              />
            </div>
            <div>
              <label className="flex items-center text-xs">
                <input type="checkbox" className="mr-2"
                  checked={formData.isMultiLoginAllowed === "1"}
                  onChange={(e) => setFormData(prev => ({ ...prev, isMultiLoginAllowed: e.target.checked ? "1" : "0" }))}
                />
                Multi-Login Allowed
              </label>
            </div>
          </div>

          <div className="flex justify-end mt-6">
            <button
              className="bg-brand-blue text-white px-8 py-3 rounded-lg hover:bg-brand-blue-dark font-medium text-sm"
              onClick={() => setActiveTab("access")}
              disabled={loading}
            >
              Next: Access & Permissions
            </button>
          </div>
        </div>
      )}

      {/* Access & Permissions Tab */}
      {activeTab === "access" && (
        <div className="p-6">
          {formData.userType === "Entity User" && <PrimaryMerchantAssignment formData={formData} setFormData={setFormData} />}

          {/* Branch Selection Table */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
            <h3 className="text-sm font-bold text-slate-900 mb-4">Branch Access Selection</h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-4 py-3 text-xs font-semibold text-left text-slate-500">
                      <input 
                        type="checkbox" 
                        className="mr-2 h-4 w-4"
                        checked={selectedBranches.length === branches.length}
                        onChange={toggleAllBranches}
                      />
                      Select All
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-left text-slate-500">Branch ID</th>
                    <th className="px-4 py-3 text-xs font-semibold text-left text-slate-500">Branch Name</th>
                    <th className="px-4 py-3 text-xs font-semibold text-left text-slate-500">Location</th>
                    <th className="px-4 py-3 text-xs font-semibold text-left text-slate-500">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {branches.map((branch) => (
                    <tr key={branch.id} className="border-b hover:bg-slate-50">
                      <td className="p-3">
                        <input 
                          type="checkbox" 
                          className="h-4 w-4"
                          checked={selectedBranches.includes(branch.id)}
                          onChange={() => toggleBranchSelection(branch.id)}
                        />
                      </td>
                      <td className="p-3 text-sm">{branch.id}</td>
                      <td className="p-3 text-sm font-medium">{branch.name}</td>
                      <td className="p-3 text-sm">{branch.location}</td>
                      <td className="p-3 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          branch.status === 'Active' 
                            ? 'bg-emerald-50 text-emerald-700' 
                            : 'bg-slate-100 text-slate-600'
                        }`}>
                          {branch.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 text-sm text-slate-500">
              Selected Branches: {selectedBranches.length} of {branches.length}
            </div>
          </div>

          {/* Permissions Table */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
            <h3 className="text-sm font-bold text-slate-900 mb-4">Module Permissions</h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-4 py-3 text-xs font-semibold text-left text-slate-500">Module</th>
                    <th className="px-4 py-3 text-xs font-semibold text-center text-slate-500">View</th>
                    <th className="px-4 py-3 text-xs font-semibold text-center text-slate-500">Create</th>
                    <th className="px-4 py-3 text-xs font-semibold text-center text-slate-500">Edit</th>
                    <th className="px-4 py-3 text-xs font-semibold text-center text-slate-500">Delete</th>
                    <th className="px-4 py-3 text-xs font-semibold text-center text-slate-500">All</th>
                  </tr>
                </thead>
                <tbody>
                  {permissionsState.map((perm) => (
                    <tr key={perm.id} className="border-b hover:bg-slate-50">
                      <td className="p-3 text-sm font-medium">{perm.module}</td>
                      <td className="text-center p-3">
                        <input 
                          type="checkbox" 
                          className="h-4 w-4"
                          checked={perm.view}
                          onChange={() => togglePermission(perm.id, 'view')}
                        />
                      </td>
                      <td className="text-center p-3">
                        <input 
                          type="checkbox" 
                          className="h-4 w-4"
                          checked={perm.create}
                          onChange={() => togglePermission(perm.id, 'create')}
                        />
                      </td>
                      <td className="text-center p-3">
                        <input 
                          type="checkbox" 
                          className="h-4 w-4"
                          checked={perm.edit}
                          onChange={() => togglePermission(perm.id, 'edit')}
                        />
                      </td>
                      <td className="text-center p-3">
                        <input 
                          type="checkbox" 
                          className="h-4 w-4"
                          checked={perm.delete}
                          onChange={() => togglePermission(perm.id, 'delete')}
                        />
                      </td>
                      <td className="text-center p-3">
                        <button
                          onClick={() => toggleAllPermissionsForModule(perm.id)}
                          className="text-brand-blue hover:text-brand-blue-dark text-xs font-medium"
                        >
                          Toggle All
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-between mt-10">
            <button
              className="border border-slate-300 text-slate-700 px-6 py-3 rounded-lg text-sm"
              onClick={() => setActiveTab("basic")}
            >
              Previous
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className={`px-10 py-3 rounded-lg font-medium text-white ${loading ? "bg-slate-400" : "bg-brand-blue hover:bg-brand-blue-dark"}`}
            >
              {loading ? "Creating User..." : "Create User"}
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  )
}